import React, { useState, useEffect } from 'react';
import { checkSignIn, openAccount, lastService, goToService, dispatcherForgotAvailable, dispatcherForgot, dispatcherReset } from '../traccar';
import SetPasswordForm from './SetPasswordForm';
import Logo from '../Logo';
import ServiceTabs from './ServiceTabs';

const inp = { padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', width: '100%', boxSizing: 'border-box', background: '#fff' };

// Admin gate for the Dynamic Dispatch dashboard. Only a Traccar administrator
// account can sign in here; on success the whole fleet is visible.
export default function AdminLoginPage({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [choices, setChoices] = useState(null); // more than one account on this email → a tab per service
  // forgot password (dispatchers; once the Jarvis mailbox is connected) and the reset link
  const [canForgot, setCanForgot] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [note, setNote] = useState('');
  const [resetToken] = useState(() => { try { return new URLSearchParams(window.location.search).get('reset') || ''; } catch { return ''; } });
  const [resetDone, setResetDone] = useState(false);
  useEffect(() => { dispatcherForgotAvailable().then(setCanForgot); }, []);
  const askLink = async (e) => { e.preventDefault(); setErr(''); setBusy(true); try { const r = await dispatcherForgot(email.trim()); setNote(r.message || 'Check your email for the link.'); } catch (ex) { setErr(ex.message); } finally { setBusy(false); } };
  const leaveReset = () => { try { const u = new URL(window.location.href); u.searchParams.delete('reset'); window.history.replaceState(null, '', u.toString()); } catch { /* ignore */ } };

  const openService = (acc) => {
    const user = openAccount(acc);
    if (user.kind === 'admin' || user.kind === 'dispatcher') onLoggedIn && onLoggedIn({ ...user, role: user.kind === 'dispatcher' ? 'dispatcher' : user.role });
    else goToService(user);
  };

  const submit = async (e) => {
    e && e.preventDefault();
    setErr(''); setBusy(true);
    try {
      // one sign-in for every account: admin → dashboard, dispatcher → console, anyone else → their service
      const j = await checkSignIn(email.trim(), password);
      if (j.accounts && j.accounts.length > 1) setChoices(j.accounts); else openService(j);
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg,#0a1a2f,#12325a)', padding: '20px' }}>
      <div style={{ width: 'min(400px, 96vw)', background: 'white', borderRadius: '18px', padding: '28px', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', marginBottom: '4px' }}>
          <Logo size={40} />
          <div>
            <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '20px', color: '#0f172a', lineHeight: 1.1 }}>Dynamic Dispatch</div>
            <span style={{ fontSize: '9px', letterSpacing: '1.5px', color: '#0284c7', fontWeight: 700 }}>BY DYNAMICS BPO</span>
          </div>
        </div>
        <div style={{ height: 18 }} />

        {choices ? (
          <div>
            <ServiceTabs accounts={choices} last={lastService(email)} onPick={openService} />
            <button type="button" onClick={() => setChoices(null)} style={{ width: '100%', marginTop: 10, background: 'transparent', border: 'none', color: '#0047d0', fontWeight: 700, cursor: 'pointer' }}>‹ Back</button>
          </div>
        ) : resetToken && !resetDone ? (
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: '4px 0 10px' }}>Set a new password</div>
            <SetPasswordForm needCurrent={false} cta="Save new password" onSave={async (_c, next) => { const r = await dispatcherReset(resetToken, next); setEmail(r.email || ''); setResetDone(true); setNote('Password saved — sign in with it now.'); leaveReset(); }} />
          </div>
        ) : forgot ? (
          <form onSubmit={askLink}>
            <div style={{ fontSize: 14, color: '#334155', marginBottom: 10 }}>Type your dispatcher email — we'll email you a link to set a new password.</div>
            <input style={{ ...inp, margin: '5px 0 12px' }} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@floridabeauty.us" />
            {err && <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '10px', fontWeight: 600 }}>{err}</div>}
            {note && <div role="status" style={{ fontSize: '13px', color: '#166534', marginBottom: '10px', fontWeight: 600 }}>{note}</div>}
            <button type="submit" disabled={busy || !email} style={{ width: '100%', background: busy ? '#94a3b8' : 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 800, fontSize: '15px', border: 'none', borderRadius: '10px', padding: '13px', cursor: busy ? 'default' : 'pointer' }}>{busy ? 'Sending…' : 'Email me a reset link'}</button>
            <button type="button" onClick={() => { setForgot(false); setNote(''); setErr(''); }} style={{ width: '100%', marginTop: 8, background: 'transparent', border: 'none', color: '#0047d0', fontWeight: 700, cursor: 'pointer' }}>Back to sign in</button>
          </form>
        ) : (
        <form onSubmit={submit}>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Email</label>
          <input style={{ ...inp, margin: '5px 0 12px' }} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dynamicsbpo.com" />

          <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Password</label>
          <div style={{ position: 'relative', margin: '5px 0 12px' }}>
            <input style={{ ...inp, paddingRight: '46px' }} type={showPwd ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            <button type="button" onClick={() => setShowPwd((s) => !s)} title={showPwd ? 'Hide' : 'Show'}
              style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '4px 8px' }}>{showPwd ? '🙈' : '👁️'}</button>
          </div>

          {err && <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '10px', fontWeight: 600 }}>{err}</div>}

          <button type="submit" disabled={busy || !email || !password}
            style={{ width: '100%', background: busy ? '#94a3b8' : 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 800, fontSize: '15px', border: 'none', borderRadius: '10px', padding: '13px', cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {note && <div role="status" style={{ fontSize: '13px', color: '#166534', marginTop: '10px', fontWeight: 600 }}>{note}</div>}
          {canForgot && <button type="button" onClick={() => { setForgot(true); setErr(''); setNote(''); }} style={{ width: '100%', marginTop: 8, background: 'transparent', border: 'none', color: '#0047d0', fontWeight: 700, cursor: 'pointer' }}>Forgot password?</button>}
        </form>
        )}

        <p style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', marginTop: '16px' }}>
          One sign-in for everything — admins, dispatchers, customers, fleets and brokers. It takes you to your service.
        </p>
      </div>
    </div>
  );
}
